import React from 'react';
import type { BookMetadata } from '~types/book';
import { BookOpen, Cloud, MoreVertical, CloudDownload } from 'lucide-react';
import { useToastStore } from '@store/useToastStore';
import { cn } from '@lib/utils';
import { useReaderUIStore } from '@store/useReaderUIStore';
import { Progress } from '../ui/Progress';
import { BookActionMenu } from './BookActionMenu';
import { unpackColorToRGB } from '@lib/cover-palette';
import { coverUrl } from '@data/covers';
import { useCoverUrl } from '@hooks/useCoverUrl';
import { formatBytes } from '@kernel/locale/format';
import { formatReadingTime } from './readingTime';

function unpackColor(packed: number): string {
    const { r, g, b } = unpackColorToRGB(packed);
    return `rgb(${r}, ${g}, ${b})`;
}

/**
 * Props for the BookListItem component.
 */
interface BookListItemProps {
    /** The metadata of the book to display. */
    book: BookMetadata;
    /** Whether the book is a "Ghost Book" (synced metadata but no content on device). */
    isGhostBook?: boolean;
    onOpen: (book: BookMetadata) => void;
    onDelete: (book: BookMetadata) => void;
    onOffload: (book: BookMetadata) => void;
    onRestore: (book: BookMetadata) => void;
}

/**
 * Displays a book item in a list view format.
 * Includes thumbnail, title, author, progress, and action menu.
 *
 * @param props - Component props.
 * @returns The rendered list item.
 */
export const BookListItem = React.memo(({ book, isGhostBook, onOpen, onDelete, onOffload, onRestore }: BookListItemProps) => {
    const showToast = useToastStore(state => state.showToast);
    const setBookId = useReaderUIStore(state => state.setCurrentBookId);

    // OPTIMIZATION: Use book.progress passed from parent (computed by useAllBooks selector)
    // instead of subscribing individually to useBookProgress.
    // This prevents ~1000 selectors running on every store update.
    const progressPercent = book.progress ? Math.round(book.progress * 100) : 0;

    const swCoverUrl = book.coverUrl || (book.coverBlob ? coverUrl(book.id) : undefined);
    const displayUrl = useCoverUrl(book.id, book.coverBlob || undefined, swCoverUrl);

    const gradientStyle = React.useMemo(() => {
        if (!book.coverPalette || book.coverPalette.length !== 5) return undefined;

        const colors = book.coverPalette.map(unpackColor);

        // We use oklab interpolation for perceptually smooth transitions and to avoid muddy colors in the middle
        // 5th color is the Center color, used as a central radial boost.
        // Base layer is linear gradient between TL and BR.
        return {
            backgroundImage: `
                radial-gradient(at top left in oklab, ${colors[0]}, transparent),
                radial-gradient(at top right in oklab, ${colors[1]}, transparent),
                radial-gradient(at bottom left in oklab, ${colors[2]}, transparent),
                radial-gradient(at bottom right in oklab, ${colors[3]}, transparent),
                radial-gradient(circle at center in oklab, ${colors[4]} 0%, transparent 125%),
                /* Base layer is white to ensure contrast and prevent transparency */
                linear-gradient(white, white)
            `
        };
    }, [book.coverPalette]);

    const handleOpen = () => {
        if (book.isOffloaded) {
            showToast("Book is offloaded. Please restore it to read.", "error");
            return;
        }
        if (isGhostBook) {
            // Trigger restore flow for Ghost Books too
            onRestore(book);
            return;
        }
        setBookId(book.id); // Set the active book ID in the store
        onOpen(book);
    };

    const durationString = formatReadingTime(book.totalChars);
    const sizeString = book.fileSize === undefined ? '' : formatBytes(book.fileSize);

    return (
        <div className="px-4 py-2" data-testid={`book-list-item-${book.id}`}>
            <div
                className={cn(
                    "flex items-center gap-4 p-2 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer group h-full border border-transparent hover:border-border",
                    (book.isOffloaded || isGhostBook) && "opacity-75"
                )}
                onClick={handleOpen}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        if (e.target !== e.currentTarget) return;
                        e.preventDefault();
                        handleOpen();
                    }
                }}
                role="button"
                tabIndex={0}
            >
                {/* Thumbnail */}
                <div
                    className="flex-none w-10 h-14 bg-muted rounded overflow-hidden shadow-sm relative flex flex-col"
                    style={!displayUrl && gradientStyle ? gradientStyle : undefined}
                >
                    {displayUrl ? (
                        <img
                            src={displayUrl}
                            alt={`Cover for ${book.title}`}
                            className={cn(
                                "w-full h-full object-cover",
                                isGhostBook && "opacity-60"
                            )}
                        />
                    ) : !gradientStyle && (
                        <div className="w-full h-full flex items-center justify-center bg-secondary text-muted-foreground">
                            <BookOpen className="w-5 h-5" />
                        </div>
                    )}

                    {/* Offloaded overlay - file was removed to save space */}
                    {book.isOffloaded && !isGhostBook && (
                        <div
                            className="absolute inset-0 bg-black/20 flex items-center justify-center"
                            data-testid="offloaded-overlay"
                        >
                            <Cloud className="w-5 h-5 text-white drop-shadow-md" aria-hidden="true" />
                        </div>
                    )}

                    {/* Ghost Book overlay - content not on this device */}
                    {isGhostBook && !book.isOffloaded && (
                        <div
                            className="absolute inset-0 bg-black/10 flex items-center justify-center"
                            data-testid="ghost-book-overlay"
                        >
                            <CloudDownload className="w-6 h-6 text-white drop-shadow-md" />
                        </div>
                    )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0 flex flex-col justify-center">
                    {/* lang: book-sourced text carries the CONTENT language (i18n ADR §3) */}
                    <h3 lang={book.language} className="text-sm font-semibold truncate text-foreground pr-2" title={book.title}>
                        {book.title}
                    </h3>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5 truncate">
                        <span lang={book.language} className="truncate max-w-[150px]">{book.author}</span>

                        {progressPercent > 0 && (
                            <>
                                <span>•</span>
                                <div className="flex items-center gap-1.5">
                                    <Progress value={progressPercent} className="w-12 h-1.5" aria-label="Reading progress" />
                                    <span>{progressPercent}%</span>
                                </div>
                            </>
                        )}
                        {progressPercent === 0 && (
                            <>
                                <span>•</span>
                                <span>0%</span>
                            </>
                        )}

                        {durationString ? (
                            <>
                                <span>•</span>
                                <span>{durationString}</span>
                            </>
                        ) : (
                            book.fileSize !== undefined && (
                                <>
                                    <span>•</span>
                                    <span>{sizeString}</span>
                                </>
                            )
                        )}

                        {book.isOffloaded && (
                            <span className="text-amber-500 font-medium ml-1">(Offloaded)</span>
                        )}
                        {isGhostBook && !book.isOffloaded && (
                            <span className="text-blue-500 font-medium ml-1">(Cloud)</span>
                        )}
                    </div>
                </div>

                {/* Actions */}
                <div className="flex-none" onClick={(e) => e.stopPropagation()}>
                    <BookActionMenu
                        book={book}
                        onDelete={() => onDelete(book)}
                        onOffload={() => onOffload(book)}
                        onRestore={() => onRestore(book)}
                    >
                        <div
                            className={cn(
                                "p-2 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors focus:opacity-100 touch-manipulation cursor-pointer",
                                "opacity-100 md:opacity-0 md:group-hover:opacity-100 focus-within:opacity-100 focus-visible:opacity-100 md:focus-within:opacity-100" // Always visible on mobile
                            )}
                            data-testid={`book-actions-${book.id}`}
                            aria-label={`Options for ${book.title}`}
                        >
                            <MoreVertical className="w-4 h-4" />
                            <span className="sr-only">Book options</span>
                        </div>
                    </BookActionMenu>
                </div>
            </div>
        </div>
    );
});
